import { useState, useMemo } from "react";
import {
  ComposedChart, Area, Line,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer,
} from "recharts";

/* ─────────────────────────────────────────────────────────────────────────────
   STRATEGY DEFINITIONS  –  every payoff is mathematically correct
   ───────────────────────────────────────────────────────────────────────────── */
const STRATEGIES = [
  /* ── BASICS ─────────────────────────────────────────────────────── */
  {
    id:"long_call", name:"Long Call", cat:"basics", emoji:"📈", color:"#22d3ee",
    tags:[{l:"Bullish",c:"bull"},{l:"Limited Risk",c:"safe"},{l:"Unlimited Reward",c:"reward"}],
    desc:"Pay a premium for the right to BUY shares at strike K.  Your loss can never exceed the premium paid, but profits are unlimited as the stock climbs.",
    legs:[
      {n:1,act:"BUY", inst:"Call Option",   detail:"at Strike K",    cash:"− C (premium out)",   pos:false},
    ],
    guide:[
      "Choose a strike K — usually at- or slightly out-of-the-money.",
      "Buy 1 call contract and pay premium C upfront.  This is your max loss.",
      "At expiry: if S > K, intrinsic value = S − K.  Your net P&L = (S − K) − C.",
      "If S ≤ K the option expires worthless; you lose exactly C.",
    ],
    metrics:{profit:"Unlimited  (S → ∞)",loss:"C  (premium paid)",be:"K + C"},
    params:{K:100,C:5},
    defs:[
      {k:"K",  label:"Strike Price (K)", min:70, max:130, step:1,   pre:"$"},
      {k:"C",  label:"Call Premium (C)", min:0.5,max:20,  step:0.5, pre:"$"},
    ],
    refs:["K"],
    payoff:(S,p)=> Math.max(0,S-p.K)-p.C,
  },
  {
    id:"long_put", name:"Long Put", cat:"basics", emoji:"📉", color:"#f87171",
    tags:[{l:"Bearish",c:"bear"},{l:"Limited Risk",c:"safe"},{l:"High Downside Reward",c:"reward"}],
    desc:"Pay a premium for the right to SELL shares at strike K.  Profits as the stock falls below K.  Max loss is the premium.",
    legs:[
      {n:1,act:"BUY", inst:"Put Option",    detail:"at Strike K",    cash:"− P (premium out)",   pos:false},
    ],
    guide:[
      "Choose a strike K — usually at- or slightly out-of-the-money.",
      "Buy 1 put contract and pay premium P upfront.",
      "At expiry: if S < K, intrinsic value = K − S.  Net P&L = (K − S) − P.",
      "If S ≥ K the put expires worthless; you lose exactly P.",
    ],
    metrics:{profit:"K − P  (if stock → $0)",loss:"P  (premium paid)",be:"K − P"},
    params:{K:100,P:5},
    defs:[
      {k:"K",  label:"Strike Price (K)", min:70, max:130, step:1,   pre:"$"},
      {k:"P",  label:"Put Premium (P)",  min:0.5,max:20,  step:0.5, pre:"$"},
    ],
    refs:["K"],
    payoff:(S,p)=> Math.max(0,p.K-S)-p.P,
  },
  {
    id:"short_call", name:"Short Call", cat:"basics", emoji:"⬇️", color:"#fb923c",
    tags:[{l:"Bearish/Neutral",c:"bear"},{l:"Unlimited Risk",c:"danger"},{l:"Limited Reward",c:"cap"}],
    desc:"Sell a call and collect the premium now.  Profit if the stock stays below K, but face unlimited loss if it surges above K.",
    legs:[
      {n:1,act:"SELL",inst:"Call Option",   detail:"at Strike K",    cash:"+ C (premium in)",    pos:true},
    ],
    guide:[
      "Write (sell) 1 call at strike K — receive premium C immediately.",
      "C is your absolute maximum profit.",
      "If S rises above K you must deliver shares at K.  Loss grows without limit above K + C.",
      "Requires margin.  Only suitable when you are confident the stock won't spike.",
    ],
    metrics:{profit:"C  (premium received)",loss:"Unlimited  (S → ∞)",be:"K + C"},
    params:{K:100,C:5},
    defs:[
      {k:"K",  label:"Strike Price (K)", min:70, max:130, step:1,   pre:"$"},
      {k:"C",  label:"Call Premium (C)", min:0.5,max:20,  step:0.5, pre:"$"},
    ],
    refs:["K"],
    payoff:(S,p)=> p.C-Math.max(0,S-p.K),
  },
  {
    id:"short_put", name:"Short Put", cat:"basics", emoji:"⬆️", color:"#a78bfa",
    tags:[{l:"Bullish/Neutral",c:"bull"},{l:"High Risk",c:"danger"},{l:"Limited Reward",c:"cap"}],
    desc:"Sell a put and collect the premium.  Profit if the stock stays above K, but you're obligated to buy shares at K if it falls hard.",
    legs:[
      {n:1,act:"SELL",inst:"Put Option",    detail:"at Strike K",    cash:"+ P (premium in)",    pos:true},
    ],
    guide:[
      "Write (sell) 1 put at strike K — receive premium P immediately.",
      "P is your absolute maximum profit.",
      "If S falls below K you must buy shares at K.  Loss floor = K − P (stock → $0).",
      "Requires margin.  Popular as a 'cash-secured put' when you want to acquire shares cheaply.",
    ],
    metrics:{profit:"P  (premium received)",loss:"K − P  (stock → $0)",be:"K − P"},
    params:{K:100,P:5},
    defs:[
      {k:"K",  label:"Strike Price (K)", min:70, max:130, step:1,   pre:"$"},
      {k:"P",  label:"Put Premium (P)",  min:0.5,max:20,  step:0.5, pre:"$"},
    ],
    refs:["K"],
    payoff:(S,p)=> p.P-Math.max(0,p.K-S),
  },

  /* ── SPREADS ─────────────────────────────────────────────────────── */
  {
    id:"bull_call_spread", name:"Bull Call Spread", cat:"spreads", emoji:"🐂", color:"#34d399",
    tags:[{l:"Bullish",c:"bull"},{l:"Limited Risk",c:"safe"},{l:"Limited Reward",c:"cap"},{l:"Net Debit",c:"debit"}],
    desc:"Buy a lower-strike call (K₁) and sell a higher-strike call (K₂).  Lower cost than a naked call, but upside is capped at K₂.",
    legs:[
      {n:1,act:"BUY", inst:"Call Option",   detail:"Lower Strike K₁",  cash:"− C₁",pos:false},
      {n:2,act:"SELL",inst:"Call Option",   detail:"Upper Strike K₂",  cash:"+ C₂",pos:true},
    ],
    guide:[
      "K₂ must be greater than K₁.  Both options share the same expiry.",
      "Buy 1 call at K₁ (ATM or slightly ITM) — pay C₁.",
      "Sell 1 call at K₂ (OTM) — receive C₂.  Net debit = C₁ − C₂.",
      "Max profit = (K₂ − K₁) − (C₁ − C₂)  when S ≥ K₂ at expiry.",
      "Max loss = net debit (C₁ − C₂)  when S ≤ K₁ at expiry.",
    ],
    metrics:{profit:"(K₂−K₁) − (C₁−C₂)",loss:"Net debit  C₁ − C₂",be:"K₁ + (C₁ − C₂)"},
    params:{K1:95,K2:110,C1:8,C2:3},
    defs:[
      {k:"K1", label:"Lower Strike K₁",  min:60, max:120, step:1,   pre:"$"},
      {k:"K2", label:"Upper Strike K₂",  min:70, max:145, step:1,   pre:"$"},
      {k:"C1", label:"Call Premium C₁ (at K₁)", min:0.5,max:25, step:0.5,pre:"$"},
      {k:"C2", label:"Call Premium C₂ (at K₂)", min:0.5,max:20, step:0.5,pre:"$"},
    ],
    refs:["K1","K2"],
    payoff:(S,p)=> Math.max(0,S-p.K1)-Math.max(0,S-p.K2)-(p.C1-p.C2),
  },
  {
    id:"bear_put_spread", name:"Bear Put Spread", cat:"spreads", emoji:"🐻", color:"#f472b6",
    tags:[{l:"Bearish",c:"bear"},{l:"Limited Risk",c:"safe"},{l:"Limited Reward",c:"cap"},{l:"Net Debit",c:"debit"}],
    desc:"Buy a higher-strike put (K₂) and sell a lower-strike put (K₁).  Bearish strategy at reduced cost, with capped maximum profit.",
    legs:[
      {n:1,act:"BUY", inst:"Put Option",    detail:"Higher Strike K₂",  cash:"− P₂",pos:false},
      {n:2,act:"SELL",inst:"Put Option",    detail:"Lower Strike K₁",   cash:"+ P₁",pos:true},
    ],
    guide:[
      "K₂ must be greater than K₁.  Both options share the same expiry.",
      "Buy 1 put at K₂ (ATM or slightly ITM) — pay P₂.",
      "Sell 1 put at K₁ (OTM, lower) — receive P₁.  Net debit = P₂ − P₁.",
      "Max profit = (K₂ − K₁) − (P₂ − P₁)  when S ≤ K₁ at expiry.",
      "Max loss = net debit (P₂ − P₁)  when S ≥ K₂ at expiry.",
    ],
    metrics:{profit:"(K₂−K₁) − (P₂−P₁)",loss:"Net debit  P₂ − P₁",be:"K₂ − (P₂ − P₁)"},
    params:{K1:90,K2:105,P1:2,P2:7},
    defs:[
      {k:"K1", label:"Lower Strike K₁",  min:60, max:120, step:1,   pre:"$"},
      {k:"K2", label:"Upper Strike K₂",  min:70, max:140, step:1,   pre:"$"},
      {k:"P2", label:"Put Premium P₂ (at K₂)", min:0.5,max:25, step:0.5,pre:"$"},
      {k:"P1", label:"Put Premium P₁ (at K₁)", min:0.5,max:20, step:0.5,pre:"$"},
    ],
    refs:["K1","K2"],
    payoff:(S,p)=> Math.max(0,p.K2-S)-Math.max(0,p.K1-S)-(p.P2-p.P1),
  },

  /* ── VOLATILITY ──────────────────────────────────────────────────── */
  {
    id:"straddle", name:"Long Straddle", cat:"volatility", emoji:"⚡", color:"#fbbf24",
    tags:[{l:"Neutral",c:"neutral"},{l:"Big Move Required",c:"vol"},{l:"Limited Risk",c:"safe"}],
    desc:"Buy an ATM call AND an ATM put at the SAME strike K.  You profit from a large move in either direction — direction-agnostic volatility bet.",
    legs:[
      {n:1,act:"BUY",inst:"Call Option",    detail:"Strike K  (ATM)",   cash:"− C (premium out)",pos:false},
      {n:2,act:"BUY",inst:"Put Option",     detail:"Strike K  (ATM)",   cash:"− P (premium out)",pos:false},
    ],
    guide:[
      "Both legs use the SAME strike K (usually ATM) and same expiry.",
      "Buy 1 call — pay C.   Buy 1 put — pay P.   Total cost = C + P.",
      "At expiry: P&L = max(0, S−K) + max(0, K−S) − (C+P).",
      "Upper breakeven = K + (C+P).   Lower breakeven = K − (C+P).",
      "Maximum loss occurs if stock pins exactly at K — both options expire worthless.",
    ],
    metrics:{profit:"Unlimited (up) / K−(C+P) (down)",loss:"C + P  (stock pins at K)",be:"K±(C+P)"},
    params:{K:100,C:5,P:5},
    defs:[
      {k:"K", label:"Strike K (same for both)", min:70, max:130, step:1,  pre:"$"},
      {k:"C", label:"Call Premium (C)",         min:0.5,max:20,  step:0.5,pre:"$"},
      {k:"P", label:"Put Premium (P)",          min:0.5,max:20,  step:0.5,pre:"$"},
    ],
    refs:["K"],
    payoff:(S,p)=> Math.max(0,S-p.K)+Math.max(0,p.K-S)-(p.C+p.P),
  },
  {
    id:"strangle", name:"Long Strangle", cat:"volatility", emoji:"🌀", color:"#60a5fa",
    tags:[{l:"Neutral",c:"neutral"},{l:"Big Move Required",c:"vol"},{l:"Cheaper than Straddle",c:"safe"}],
    desc:"Buy an OTM put at K₁ and an OTM call at K₂ (K₁ < K₂).  Cheaper than a straddle, but requires an even larger move to profit.",
    legs:[
      {n:1,act:"BUY",inst:"Put Option",     detail:"OTM Put Strike K₁ (lower)",  cash:"− P (premium out)",pos:false},
      {n:2,act:"BUY",inst:"Call Option",    detail:"OTM Call Strike K₂ (higher)", cash:"− C (premium out)",pos:false},
    ],
    guide:[
      "K₁ < current stock price < K₂  (both out-of-the-money).",
      "Buy 1 OTM put at K₁ — pay P.   Buy 1 OTM call at K₂ — pay C.",
      "Total cost = P + C  (cheaper than a straddle, OTM premiums are lower).",
      "Lower breakeven = K₁ − (P+C).   Upper breakeven = K₂ + (P+C).",
      "Loss if stock stays between K₁ and K₂ at expiry.",
    ],
    metrics:{profit:"Unlimited (up) / K₁−(C+P) (down)",loss:"C + P  (stock between K₁ and K₂)",be:"K₁−(C+P) and K₂+(C+P)"},
    params:{K1:92,K2:108,P:3,C:3},
    defs:[
      {k:"K1", label:"OTM Put Strike K₁",  min:60, max:115, step:1,   pre:"$"},
      {k:"K2", label:"OTM Call Strike K₂", min:75, max:140, step:1,   pre:"$"},
      {k:"P",  label:"Put Premium (P)",    min:0.5,max:15,  step:0.5, pre:"$"},
      {k:"C",  label:"Call Premium (C)",   min:0.5,max:15,  step:0.5, pre:"$"},
    ],
    refs:["K1","K2"],
    payoff:(S,p)=> Math.max(0,p.K1-S)+Math.max(0,S-p.K2)-(p.C+p.P),
  },

  /* ── INCOME ──────────────────────────────────────────────────────── */
  {
    id:"iron_condor", name:"Iron Condor", cat:"income", emoji:"🦅", color:"#818cf8",
    tags:[{l:"Neutral",c:"neutral"},{l:"Range-Bound",c:"range"},{l:"Net Credit",c:"credit"},{l:"4 Legs",c:"complex"}],
    desc:"Sell an OTM put spread + sell an OTM call spread simultaneously.  Collect net credit.  Max profit if stock stays between K₂ and K₃ at expiry.",
    legs:[
      {n:1,act:"BUY", inst:"Put Option",  detail:"K₁ — lowest  (protection wing)",  cash:"− P₁",pos:false},
      {n:2,act:"SELL",inst:"Put Option",  detail:"K₂ — OTM short put",              cash:"+ P₂",pos:true},
      {n:3,act:"SELL",inst:"Call Option", detail:"K₃ — OTM short call",             cash:"+ C₃",pos:true},
      {n:4,act:"BUY", inst:"Call Option", detail:"K₄ — highest  (protection wing)", cash:"− C₄",pos:false},
    ],
    guide:[
      "Strikes must be in order: K₁ < K₂ < current price < K₃ < K₄.",
      "Put spread (left wing): buy put K₁, sell put K₂ — net credit (P₂ − P₁).",
      "Call spread (right wing): sell call K₃, buy call K₄ — net credit (C₃ − C₄).",
      "Total net credit = (P₂ − P₁) + (C₃ − C₄) — this is your max profit.",
      "Max loss on either side = wing width − credit  (e.g. K₂−K₁ − credit).",
    ],
    metrics:{profit:"Net credit received",loss:"Wing width − net credit",be:"K₂−credit (put) & K₃+credit (call)"},
    params:{K1:80,K2:92,K3:108,K4:120,credit:4},
    defs:[
      {k:"K1",    label:"Put Wing Strike K₁",    min:50, max:100, step:1,    pre:"$"},
      {k:"K2",    label:"Short Put Strike K₂",   min:60, max:110, step:1,    pre:"$"},
      {k:"K3",    label:"Short Call Strike K₃",  min:90, max:140, step:1,    pre:"$"},
      {k:"K4",    label:"Call Wing Strike K₄",   min:100,max:160, step:1,    pre:"$"},
      {k:"credit",label:"Net Credit Received",   min:0.5,max:12,  step:0.25, pre:"$"},
    ],
    refs:["K1","K2","K3","K4"],
    payoff:(S,p)=>{
      const ps = Math.max(0,p.K2-S)-Math.max(0,p.K1-S);
      const cs = Math.max(0,S-p.K3)-Math.max(0,S-p.K4);
      return -(ps+cs)+p.credit;
    },
  },
  {
    id:"butterfly", name:"Butterfly Spread", cat:"income", emoji:"🦋", color:"#2dd4bf",
    tags:[{l:"Neutral",c:"neutral"},{l:"Net Debit",c:"debit"},{l:"3 Strikes",c:"complex"},{l:"Precise",c:"cap"}],
    desc:"Buy 1 lower-strike call, sell 2 middle-strike calls, buy 1 higher-strike call.  Max profit if stock pins exactly at K₂ (middle) at expiry.",
    legs:[
      {n:1,act:"BUY", inst:"Call Option", detail:"Lower Strike K₁",      cash:"− C₁",pos:false},
      {n:2,act:"SELL",inst:"Call Option", detail:"Middle Strike K₂ (×2)", cash:"+ 2×C₂",pos:true},
      {n:3,act:"BUY", inst:"Call Option", detail:"Upper Strike K₃",       cash:"− C₃",pos:false},
    ],
    guide:[
      "Wings must be equidistant: K₂ − K₁ = K₃ − K₂.",
      "Buy 1 call at K₁ — pay C₁.   Sell 2 calls at K₂ — receive 2×C₂.   Buy 1 call at K₃ — pay C₃.",
      "Net debit = C₁ + C₃ − 2×C₂  (should be small & positive).",
      "Max profit = (K₂ − K₁) − net debit  when stock closes exactly at K₂.",
      "Breakeven: K₁ + debit (lower) and K₃ − debit (upper).",
    ],
    metrics:{profit:"(K₂−K₁) − net debit  (stock pins at K₂)",loss:"Net debit = C₁+C₃−2×C₂",be:"K₁+debit  &  K₃−debit"},
    params:{K1:90,K2:100,K3:110,C1:12,C2:7,C3:3},
    defs:[
      {k:"K1", label:"Lower Strike K₁",   min:60, max:115, step:1,   pre:"$"},
      {k:"K2", label:"Middle Strike K₂",  min:70, max:130, step:1,   pre:"$"},
      {k:"K3", label:"Upper Strike K₃",   min:80, max:145, step:1,   pre:"$"},
      {k:"C1", label:"Call Premium C₁ (at K₁)", min:1,  max:30, step:0.5,pre:"$"},
      {k:"C2", label:"Call Premium C₂ (at K₂)", min:0.5,max:25, step:0.5,pre:"$"},
      {k:"C3", label:"Call Premium C₃ (at K₃)", min:0.5,max:20, step:0.5,pre:"$"},
    ],
    refs:["K1","K2","K3"],
    payoff:(S,p)=>{
      const gross = Math.max(0,S-p.K1) - 2*Math.max(0,S-p.K2) + Math.max(0,S-p.K3);
      const debit = p.C1 - 2*p.C2 + p.C3;
      return gross - debit;
    },
  },
  {
    id:"covered_call", name:"Covered Call", cat:"income", emoji:"🛡️", color:"#f59e0b",
    tags:[{l:"Mildly Bullish",c:"bull"},{l:"Stock Required",c:"stock"},{l:"Income",c:"credit"},{l:"Capped Upside",c:"cap"}],
    desc:"Own 100 shares (bought at S₀), then sell 1 call at strike K above your cost.  Earns premium income but caps your upside at K.",
    legs:[
      {n:1,act:"BUY", inst:"Stock  (100 shares)", detail:"Purchased at price S₀",      cash:"− S₀ × 100",pos:false},
      {n:2,act:"SELL",inst:"Call Option",         detail:"Strike K  (above S₀, OTM)",  cash:"+ C (premium in)",pos:true},
    ],
    guide:[
      "⚠️  You MUST already own (or simultaneously buy) 100 shares at S₀.",
      "Sell 1 call at strike K (choose K above S₀ — typically 5–10% OTM).",
      "Effective cost basis is reduced to S₀ − C.",
      "If S stays below K at expiry: keep shares + pocket full premium C.",
      "If S rises above K: shares are called away at K.  Max profit = (K − S₀) + C.",
    ],
    metrics:{profit:"(K − S₀) + C  (shares called at K)",loss:"S₀ − C  (stock → $0)",be:"S₀ − C"},
    params:{S0:100,K:110,C:4},
    defs:[
      {k:"S0", label:"Stock Purchase Price S₀", min:60, max:140, step:1,   pre:"$"},
      {k:"K",  label:"Call Strike K  (above S₀)",min:65,max:160, step:1,   pre:"$"},
      {k:"C",  label:"Call Premium (C)",         min:0.5,max:20, step:0.5, pre:"$"},
    ],
    refs:["S0","K"],
    payoff:(S,p)=> (S-p.S0) - Math.max(0,S-p.K) + p.C,
  },

  /* ── HEDGING ─────────────────────────────────────────────────────── */
  {
    id:"protective_put", name:"Protective Put", cat:"hedging", emoji:"🔐", color:"#e879f9",
    tags:[{l:"Bullish",c:"bull"},{l:"Stock Required",c:"stock"},{l:"Insurance",c:"safe"},{l:"Downside Floor",c:"safe"}],
    desc:"Own 100 shares (at S₀) and buy a put at strike K as portfolio insurance.  Sets a hard floor on losses while keeping unlimited upside.",
    legs:[
      {n:1,act:"BUY",inst:"Stock  (100 shares)", detail:"Purchased at price S₀",   cash:"− S₀ × 100",pos:false},
      {n:2,act:"BUY",inst:"Put Option",          detail:"Strike K  (your floor)",  cash:"− P (insurance premium)",pos:false},
    ],
    guide:[
      "⚠️  You MUST already own (or simultaneously buy) 100 shares at S₀.",
      "Buy 1 put at strike K — pay premium P.  K is your guaranteed exit price on the downside.",
      "Total outlay = S₀ + P per share.  Breakeven = S₀ + P.",
      "If stock falls below K: put gains in value, offsetting stock loss.  Loss is floored at (S₀ − K) + P.",
      "If stock rises: put expires worthless — you keep all gains, only cost was P.",
    ],
    metrics:{profit:"Unlimited  (full upside retained minus P)",loss:"(S₀ − K) + P  (hard floor at K)",be:"S₀ + P"},
    params:{S0:100,K:93,P:3},
    defs:[
      {k:"S0", label:"Stock Purchase Price S₀",  min:60, max:140, step:1,   pre:"$"},
      {k:"K",  label:"Put Strike / Floor (K)",    min:50, max:138, step:1,   pre:"$"},
      {k:"P",  label:"Insurance Premium (P)",     min:0.5,max:20,  step:0.5, pre:"$"},
    ],
    refs:["S0","K"],
    payoff:(S,p)=> (S-p.S0) + Math.max(0,p.K-S) - p.P,
  },
];

const CATS = [
  {id:"all",      label:"All",        icon:"◈"},
  {id:"basics",   label:"Basics",     icon:"①"},
  {id:"spreads",  label:"Spreads",    icon:"↔"},
  {id:"volatility",label:"Volatility",icon:"⚡"},
  {id:"income",   label:"Income",     icon:"$"},
  {id:"hedging",  label:"Hedging",    icon:"◉"},
];

const TC = {
  bull:"#bbf7d0 bg-emerald-900/30 border-emerald-600/30",
  bear:"#fca5a5 bg-red-900/30 border-red-600/30",
  neutral:"#93c5fd bg-blue-900/30 border-blue-600/30",
  safe:"#6ee7b7 bg-teal-900/30 border-teal-600/30",
  danger:"#fdba74 bg-orange-900/30 border-orange-600/30",
  reward:"#d9f99d bg-lime-900/30 border-lime-600/30",
  cap:"#cbd5e1 bg-slate-700/30 border-slate-500/30",
  debit:"#fde68a bg-yellow-900/30 border-yellow-600/30",
  credit:"#a5f3fc bg-cyan-900/30 border-cyan-600/30",
  range:"#c4b5fd bg-violet-900/30 border-violet-600/30",
  complex:"#f9a8d4 bg-pink-900/30 border-pink-600/30",
  stock:"#fed7aa bg-orange-900/30 border-orange-600/30",
  vol:"#e9d5ff bg-purple-900/30 border-purple-600/30",
};

function Tag({t}){
  const cls = TC[t.c]||"text-gray-300 bg-gray-700/30 border-gray-500/30";
  const [color,...rest] = cls.split(" ");
  return (
    <span style={{color}} className={`${rest.join(" ")} text-[10px] font-bold px-2 py-0.5 rounded-md border`}>{t.l}</span>
  );
}

/* ── CHART ── */
function generateData(strat, params) {
  const refs = strat.refs.map(r=>params[r]).filter(Boolean);
  if(!refs.length) return [];
  const lo = Math.min(...refs)-28, hi = Math.max(...refs)+28;
  const out = [];
  for(let S=lo;S<=hi;S+=0.5){
    const v = +strat.payoff(S,params).toFixed(2);
    out.push({S:+S.toFixed(1), payoff:v, pos:Math.max(0,v), neg:Math.min(0,v)});
  }
  return out;
}

const REF_LABEL = {K:"K",K1:"K₁",K2:"K₂",K3:"K₃",K4:"K₄",S0:"S₀"};

function ChartTip({active,payload,label}){
  if(!active||!payload?.length) return null;
  const v = payload.find(p=>p.dataKey==="payoff")?.value??0;
  return (
    <div className="bg-[#0a0e1a] border border-[#1e293b] rounded-xl p-3 shadow-2xl min-w-[120px]">
      <div className="text-[10px] text-slate-500 mb-0.5 font-mono uppercase tracking-widest">Price at Expiry</div>
      <div className="text-white font-black text-lg font-mono">${label}</div>
      <div className="text-[10px] text-slate-500 mt-2 mb-0.5 font-mono uppercase tracking-widest">Net P&L</div>
      <div className={`font-black text-xl font-mono ${v>=0?"text-emerald-400":"text-red-400"}`}>{v>=0?"+":""}{v.toFixed(2)}</div>
    </div>
  );
}

function PayoffChart({strat,params}){
  const data = useMemo(()=>generateData(strat,params),[strat,params]);
  if(!data.length) return null;
  const vals = data.map(d=>d.payoff);
  const maxV=Math.max(...vals),minV=Math.min(...vals);
  const pad=(maxV-minV)*0.2+3;
  return (
    <ResponsiveContainer width="100%" height={240}>
      <ComposedChart data={data} margin={{top:14,right:12,left:2,bottom:2}}>
        <defs>
          <linearGradient id={`gp-${strat.id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#4ade80" stopOpacity={0.4}/>
            <stop offset="100%" stopColor="#4ade80" stopOpacity={0.02}/>
          </linearGradient>
          <linearGradient id={`gn-${strat.id}`} x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor="#f87171" stopOpacity={0.4}/>
            <stop offset="100%" stopColor="#f87171" stopOpacity={0.02}/>
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="2 4" stroke="#1e293b" vertical={false}/>
        <XAxis dataKey="S" stroke="#1e293b" tick={{fill:"#475569",fontSize:9,fontFamily:"monospace"}} tickFormatter={v=>`$${v}`} tickLine={false}/>
        <YAxis stroke="#1e293b" tick={{fill:"#475569",fontSize:9,fontFamily:"monospace"}} tickFormatter={v=>`${v>0?"+":""}${v}`} domain={[minV-pad,maxV+pad]} tickLine={false} width={44}/>
        <Tooltip content={<ChartTip/>}/>
        <ReferenceLine y={0} stroke="#334155" strokeWidth={1.5} strokeDasharray="5 3"/>
        {strat.refs.map(r=>(
          <ReferenceLine key={r} x={params[r]} stroke="#334155" strokeWidth={1} strokeDasharray="3 4"
            label={{value:REF_LABEL[r]||r,fill:"#64748b",fontSize:9,fontFamily:"monospace",position:"insideTopRight"}}/>
        ))}
        <Area type="monotone" dataKey="pos" stroke="none" fill={`url(#gp-${strat.id})`} dot={false} isAnimationActive={false}/>
        <Area type="monotone" dataKey="neg" stroke="none" fill={`url(#gn-${strat.id})`} dot={false} isAnimationActive={false}/>
        <Line type="monotone" dataKey="payoff" stroke={strat.color} strokeWidth={2.5} dot={false}
          activeDot={{r:4,fill:strat.color,stroke:"#0a0e1a",strokeWidth:2}} isAnimationActive={false}/>
      </ComposedChart>
    </ResponsiveContainer>
  );
}

/* ── MAIN ── */
export default function DerivativesStrategyLab(){
  const [cat,setCat]=useState("all");
  const [sel,setSel]=useState(STRATEGIES[0]);
  const [params,setParams]=useState({...STRATEGIES[0].params});
  const [tab,setTab]=useState("build");

  const list = cat==="all"?STRATEGIES:STRATEGIES.filter(s=>s.cat===cat);

  const pick = s => { setSel(s); setParams({...s.params}); setTab("build"); };

  const liveMetrics = useMemo(()=>{
    const data = generateData(sel,params);
    if(!data.length) return null;
    const vs = data.map(d=>d.payoff);
    const mx = Math.max(...vs), mn = Math.min(...vs);
    const be = data.find((d,i)=>i>0&&((data[i-1].payoff<0&&d.payoff>=0)||(data[i-1].payoff>0&&d.payoff<=0)));
    return {mx,mn,be};
  },[sel,params]);

  return (
    <div className="min-h-screen bg-[#060b14] text-slate-100 flex flex-col" style={{fontFamily:"'DM Mono', 'Fira Mono', 'Courier New', monospace"}}>

      {/* HEADER */}
      <header className="border-b border-[#0f1e35] bg-[#060b14]/95 backdrop-blur sticky top-0 z-20">
        <div className="max-w-[1400px] mx-auto px-5 py-3 flex items-center gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center text-xl" style={{background:"rgba(34,211,238,0.1)",border:"1px solid rgba(34,211,238,0.2)"}}>📊</div>
            <div>
              <div className="text-sm font-black tracking-[-0.03em] text-white">DERIVATIVES  STRATEGY  LAB</div>
              <div className="text-[9px] text-slate-600 tracking-widest uppercase">Build · Learn · Visualise · 12 Strategies</div>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-1 text-[9px] text-slate-700 tracking-widest">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block mr-1 animate-pulse"/>PAYOFFS AT EXPIRATION
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto w-full px-4 py-5 flex gap-4 flex-1">

        {/* LEFT SIDEBAR */}
        <aside className="w-[190px] flex-shrink-0 flex flex-col gap-3">

          {/* Category */}
          <div className="rounded-xl border border-[#0f1e35] bg-[#0a0e1a] p-2">
            <div className="text-[8.5px] font-black tracking-[0.1em] text-slate-600 uppercase mb-2 px-2">Category</div>
            {CATS.map(c=>{
              const cnt = c.id==="all"?STRATEGIES.length:STRATEGIES.filter(s=>s.cat===c.id).length;
              const on = cat===c.id;
              return (
                <button key={c.id} onClick={()=>setCat(c.id)}
                  className={`w-full text-left px-2.5 py-1.5 rounded-lg mb-0.5 text-[11px] flex items-center gap-2 transition-all cursor-pointer
                    ${on?"bg-[#0f2035] text-cyan-300 border border-cyan-900/60":"text-slate-500 hover:text-slate-300 border border-transparent"}`}>
                  <span className="opacity-60 w-4 text-center">{c.icon}</span>
                  <span className="flex-1">{c.label}</span>
                  <span className="text-[9px] opacity-40">{cnt}</span>
                </button>
              );
            })}
          </div>

          {/* Strategy list */}
          <div className="rounded-xl border border-[#0f1e35] bg-[#0a0e1a] p-2 flex-1 overflow-y-auto">
            <div className="text-[8.5px] font-black tracking-[0.1em] text-slate-600 uppercase mb-2 px-2">Strategies</div>
            {list.map(s=>{
              const on = sel.id===s.id;
              return (
                <button key={s.id} onClick={()=>pick(s)}
                  className={`w-full text-left px-2.5 py-2 rounded-lg mb-0.5 transition-all cursor-pointer border
                    ${on?"border-[#1a3050] bg-[#0d1c2e]":"border-transparent hover:bg-[#0d1620]"}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{background:on?s.color:"#1e293b"}}/>
                    <span className={`text-[11px] leading-tight ${on?"text-white font-semibold":"text-slate-400"}`}>{s.name}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="rounded-xl border border-[#0f1e35] bg-[#0a0e1a] p-3">
            <div className="text-[8.5px] font-black tracking-[0.1em] text-slate-600 uppercase mb-2">Chart Guide</div>
            {[
              {color:"#4ade80",label:"Profit (green)"},
              {color:"#f87171",label:"Loss (red)"},
              {color:"#334155",label:"Strike ref. line"},
            ].map(r=>(
              <div key={r.label} className="flex items-center gap-2 mb-1.5">
                <div className="w-5 h-[2px] rounded flex-shrink-0" style={{background:r.color}}/>
                <span className="text-[9px] text-slate-600">{r.label}</span>
              </div>
            ))}
          </div>
        </aside>

        {/* MAIN */}
        <main className="flex-1 min-w-0 flex flex-col gap-3">

          {/* Strategy header */}
          <div className="rounded-xl border bg-[#0a0e1a] p-4" style={{borderColor:`${sel.color}25`}}>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0 mt-0.5"
                style={{background:`${sel.color}12`,border:`1px solid ${sel.color}30`}}>{sel.emoji}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap mb-1.5">
                  <h1 className="text-lg font-black tracking-[-0.025em] text-white">{sel.name}</h1>
                  <div className="flex flex-wrap gap-1">
                    {sel.tags.map(t=><Tag key={t.l} t={t}/>)}
                  </div>
                </div>
                <p className="text-[12px] text-slate-400 leading-relaxed">{sel.desc}</p>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mt-4 border-t border-[#0f1e35] pt-3">
              {[
                {id:"build",  icon:"📋", label:"How to Build"},
                {id:"payoff", icon:"📈", label:"Payoff Diagram"},
              ].map(t=>(
                <button key={t.id} onClick={()=>setTab(t.id)} className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-[11px] font-bold transition-all cursor-pointer border
                  ${tab===t.id?"text-white border-[#1a3050] bg-[#0d1c2e]":"text-slate-500 border-transparent hover:text-slate-300"}`}
                  style={tab===t.id?{color:sel.color}:{}}>
                  <span>{t.icon}</span>{t.label}
                </button>
              ))}
            </div>
          </div>

          {/* ── BUILD TAB ── */}
          {tab==="build" && (
            <div className="grid grid-cols-2 gap-3">

              {/* Legs */}
              <div className="rounded-xl border border-[#0f1e35] bg-[#0a0e1a] p-4">
                <div className="text-[8.5px] font-black tracking-[0.12em] text-slate-600 uppercase mb-3">Strategy Legs</div>
                <div className="space-y-2">
                  {sel.legs.map(leg=>(
                    <div key={leg.n} className="rounded-lg border border-[#1a2535] bg-[#0d1620] p-3 flex gap-3 items-start">
                      <div className="w-5 h-5 rounded-md bg-[#1e293b] text-slate-400 text-[9px] font-black flex items-center justify-center flex-shrink-0 mt-0.5">{leg.n}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className={`text-[9px] font-black px-1.5 py-0.5 rounded tracking-wide ${leg.act==="BUY"?"bg-emerald-900/40 text-emerald-400 border border-emerald-700/40":"bg-red-900/40 text-red-400 border border-red-700/40"}`}>{leg.act}</span>
                          <span className="text-[11px] font-semibold text-slate-200">{leg.inst}</span>
                        </div>
                        <div className="text-[10px] text-slate-500 mb-1">{leg.detail}</div>
                        <div className={`text-[10px] font-mono ${leg.cash.trimStart().startsWith("+")||(leg.cash.includes("in"))?"text-emerald-400":"text-red-400"}`}>{leg.cash}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Guide + Metrics */}
              <div className="flex flex-col gap-3">
                <div className="rounded-xl border border-[#0f1e35] bg-[#0a0e1a] p-4 flex-1">
                  <div className="text-[8.5px] font-black tracking-[0.12em] text-slate-600 uppercase mb-3">Step-by-Step Construction</div>
                  <ol className="space-y-2">
                    {sel.guide.map((step,i)=>(
                      <li key={i} className="flex gap-3 items-start">
                        <span className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center text-[9px] font-black mt-0.5" style={{background:`${sel.color}15`,color:sel.color}}>{i+1}</span>
                        <span className="text-[11px] text-slate-400 leading-relaxed">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="rounded-xl border border-[#0f1e35] bg-[#0a0e1a] p-4">
                  <div className="text-[8.5px] font-black tracking-[0.12em] text-slate-600 uppercase mb-3">Key Metrics at Expiry</div>
                  <div className="space-y-2">
                    {[
                      {label:"Max Profit", val:sel.metrics.profit, color:"#4ade80", bg:"rgba(74,222,128,0.06)", border:"rgba(74,222,128,0.2)"},
                      {label:"Max Loss",   val:sel.metrics.loss,   color:"#f87171", bg:"rgba(248,113,113,0.06)",border:"rgba(248,113,113,0.2)"},
                      {label:"Breakeven",  val:sel.metrics.be,     color:"#60a5fa", bg:"rgba(96,165,250,0.06)", border:"rgba(96,165,250,0.2)"},
                    ].map(m=>(
                      <div key={m.label} className="rounded-lg px-3 py-2.5" style={{background:m.bg,border:`1px solid ${m.border}`}}>
                        <div className="text-[8.5px] font-black tracking-[0.1em] uppercase mb-1" style={{color:m.color,opacity:0.7}}>{m.label}</div>
                        <div className="text-[11.5px] font-bold leading-snug" style={{color:m.color}}>{m.val}</div>
                      </div>
                    ))}
                  </div>
                  <button onClick={()=>setTab("payoff")}
                    className="w-full mt-3 py-2 rounded-lg text-[11px] font-bold text-white cursor-pointer transition-all hover:opacity-90 border"
                    style={{background:`${sel.color}12`,borderColor:`${sel.color}35`,color:sel.color}}>
                    View Payoff Diagram →
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── PAYOFF TAB ── */}
          {tab==="payoff" && (
            <div className="grid grid-cols-2 gap-3">

              {/* Chart */}
              <div className="rounded-xl border border-[#0f1e35] bg-[#0a0e1a] p-4">
                <div className="text-[8.5px] font-black tracking-[0.12em] text-slate-600 uppercase mb-3">Payoff at Expiration  (per share)</div>
                <PayoffChart strat={sel} params={params}/>
                <div className="mt-3 flex items-center gap-4 flex-wrap">
                  {[
                    {color:"#4ade80",label:"Profit zone"},
                    {color:"#f87171",label:"Loss zone"},
                    {color:"#334155",label:"Strike reference"},
                  ].map(l=>(
                    <span key={l.label} className="flex items-center gap-1.5 text-[9px] text-slate-600">
                      <span className="w-4 h-[2px] rounded inline-block" style={{background:l.color}}/>
                      {l.label}
                    </span>
                  ))}
                </div>
              </div>

              {/* Params + Live metrics */}
              <div className="flex flex-col gap-3">
                <div className="rounded-xl border border-[#0f1e35] bg-[#0a0e1a] p-4 flex-1">
                  <div className="text-[8.5px] font-black tracking-[0.12em] text-slate-600 uppercase mb-4">🎛  Adjust Parameters</div>
                  <div className="grid grid-cols-2 gap-x-5 gap-y-4">
                    {sel.defs.map(d=>(
                      <div key={d.k}>
                        <div className="flex justify-between mb-1.5">
                          <label className="text-[10px] text-slate-500 leading-tight">{d.label}</label>
                          <span className="text-[10.5px] font-black font-mono" style={{color:sel.color}}>{d.pre}{params[d.k]}</span>
                        </div>
                        <input type="range" min={d.min} max={d.max} step={d.step} value={params[d.k]}
                          onChange={e=>setParams(prev=>({...prev,[d.k]:+e.target.value}))}
                          className="w-full h-[3px]" style={{accentColor:sel.color}}/>
                        <div className="flex justify-between text-[8.5px] text-slate-700 mt-0.5">
                          <span>{d.pre}{d.min}</span><span>{d.pre}{d.max}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {liveMetrics && (
                  <div className="rounded-xl border border-[#0f1e35] bg-[#0a0e1a] p-4">
                    <div className="text-[8.5px] font-black tracking-[0.12em] text-slate-600 uppercase mb-3">Live Calculated Metrics</div>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        {label:"Max Profit", val: liveMetrics.mx>9000?"∞":`+${liveMetrics.mx.toFixed(1)}`, color:"#4ade80"},
                        {label:"Max Loss",   val: liveMetrics.mn<-9000?"-∞":`${liveMetrics.mn.toFixed(1)}`, color:"#f87171"},
                        {label:"Breakeven",  val: liveMetrics.be?`$${liveMetrics.be.S}`:"—", color:"#60a5fa"},
                      ].map(m=>(
                        <div key={m.label} className="rounded-lg bg-[#0d1620] border border-[#1a2535] p-2.5 text-center">
                          <div className="text-[8px] font-black tracking-[0.08em] uppercase mb-1.5" style={{color:m.color,opacity:0.6}}>{m.label}</div>
                          <div className="text-[15px] font-black font-mono" style={{color:m.color}}>{m.val}</div>
                        </div>
                      ))}
                    </div>
                    <button onClick={()=>setTab("build")}
                      className="w-full mt-3 py-1.5 rounded-lg text-[10px] font-bold text-slate-500 cursor-pointer hover:text-slate-300 transition-all border border-[#1a2535] bg-transparent">
                      ← Back to Construction Guide
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
